import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {PaginationService} from "./pagination.service";
import {ClipboardService} from "./clipboard.service";
import {Product, Funnel, FunnelStep, FunnelInputTypeEnum, OfferTypes} from "../_models";
import {BehaviorSubject, Observable, from} from 'rxjs';
import {copyToClipboard} from "../_helpers/functions";
import {mergeMap, tap, map} from "rxjs/operators";

@Injectable()
export class FunnelService extends PaginationService {
  copiedFunnel: BehaviorSubject<any> = new BehaviorSubject(null);
  route = 'funnels';

  constructor(
    protected http: HttpClient,
    protected clipboardService: ClipboardService
  ) {
    super(http, 'funnels');
  }

  private addOfferSteps(funnel: Funnel): Funnel {
    funnel.offer_steps = funnel.steps ?
        funnel.steps.filter((step: FunnelStep) => step.type !== FunnelInputTypeEnum.InputActionStatus) : [];

    return funnel;
  }

  get(id): Observable<Funnel> {
    return super.get(id).pipe(map((funnel: Funnel) => {
      return this.addOfferSteps(funnel);
    }));
  }

  update(id, funnel: Funnel) {
    return super.update(id, funnel).pipe(map((funnel: Funnel) => {
      return this.addOfferSteps(funnel);
    }));
  }

  patch(id, data: {}) {
    return super.patch(id, data).pipe(map((funnel: Funnel) => {
      return this.addOfferSteps(funnel);
    }));
  }

  create(obj: any) {
    return super.create(obj).pipe(map((funnel: Funnel) => {
      return this.addOfferSteps(funnel);
    }));
  }

  listRetentionOffers(
    campaignId: number | string,
    offerIntent: number | number[],
    offerType: OfferTypes | OfferTypes[] = null,
    excludeAudience: boolean = true,
    accountId?: number | string
   ): Observable<any> {
    let params = new HttpParams();

    if (campaignId) {
      params = params.set("campaign", campaignId.toString());
    } else if (accountId) {
      params = params.set("account", accountId.toString());
    } else {
      if (offerType !== OfferTypes.SingleStep) {
        params = params.set("account__isnull", "true");
      }
    }

    if (Array.isArray(offerIntent)) {
      params = params.set("offer_intent__in", offerIntent.join(","));
    } else if (offerIntent) {
      params = params.set("offer_intent", offerIntent.toString());
    }

    if (Array.isArray(offerType) && offerType.length > 0) {
      params = params.set("offer_type__in", offerType.join(","));
    } else if (offerType) {
      params = params.set("offer_type", offerType.toString());
    }

    if (excludeAudience) {
      params = params.set("audience__isnull", "true");
    }

    params = params.set("resourcetype", "ProductFunnel");
    return this.http.get(`${this.route}/offer_list/`, { params });
  }

  listSegments(offerIntent: number | number[]): Observable<any> {
    let params = new HttpParams();

    if (Array.isArray(offerIntent)) {
      params = params.set('offer_intent__in', offerIntent.join(','));
    } else {
      params = params.set('offer_intent', offerIntent.toString());
    }

    params = params.set('offer_type', OfferTypes.Downsell.toString());
    params = params.set('audience__isnull', 'false');
    params = params.set('resourcetype', 'ProductFunnel');

    return this.http.get(`${this.route}/offer_list/`, { params });
  }

  createStage(obj: any) {
    return this.http.post(this.route + '/create_template/', obj);
  }

  copy(id: string | number) {
    this.clipboardService.copy('funnel', id.toString());
  }

  canPaste() : boolean {
    return this.clipboardService.canPaste('funnel');
  }

  paste() {
    return this.http.post(`${this.route}/${this.clipboardService.paste('funnel')}/generate/`, {});
  }

  copyProducts(products: Product[]) {
    this.clipboardService.copy('products', products);
  }

  canPasteProducts() : boolean {
    return this.clipboardService.canPaste('products');
  }

  pasteProducts() {
    return this.clipboardService.paste('products');
  }

  preview(campaignId: string | number, data: any) {
    const funnel: Funnel = data;

    return this.http.post(this.route + '/' + funnel.id.toString() + '/preview/', {campaign: campaignId});
  }

  publish(id: string | number) {
    return this.http.post(this.route + '/' + id.toString() + '/publish/', {});
  }

  getHierarchy() {
    return this.http.get(this.route + '/hierarchy/');
  }

  createTemplate(id: string | number, isPublic: boolean = false) {
    return this.http.post(this.route + '/' + id.toString() + '/template/', {is_public: isPublic});
  }

  clone(id: string | number, campaignId: string | number = null, payload: any = {}) {
    if (campaignId) {
      payload.campaign = campaignId;
    }
    return this.http.post(this.route + '/' + id.toString() + '/generate/', payload);
  }

  add_products(id: string | number, data) {
    return this.http.post(this.route + '/' + id.toString() + '/add_products/', data)
  }

  remove_product(id: string | number, data) {
    return this.http.post(this.route + '/' + id.toString() + '/remove_product/', data)
  }

  copyToClipboard(id: string | number) {
    return this.http.post(this.route + '/' + id.toString() + '/copy/', {}).pipe(
      tap(data => {
        copyToClipboard(JSON.stringify(data));
      })
    );
  }

  pasteFromClipboard() {
    return from(navigator.clipboard.readText()).pipe(
      mergeMap((text) => {
        return this.create(JSON.parse(text));
      }),
    )
  }

  previewStep(campaignId: string | number, data: any = {}, productId?: string | number) {
    const payload = { ...data, campaign: campaignId };
    if (productId) {
      payload.product = productId;
    }

    return this.http.post('funnel_steps/preview/', payload);
  }

  drop(id, data: {}) {
    return this.http.patch(this.route + '/' + id.toString() + '/drop/', data);
  }
}
